export enum DocumentCategory {
  PDF = 'PDF',
  IMAGE = 'Image',
  EMAIL = 'Email',
  OTHER = 'Other',
}

const CATEGORY_ORDER: readonly DocumentCategory[] = [
  DocumentCategory.PDF,
  DocumentCategory.IMAGE,
  DocumentCategory.EMAIL,
  DocumentCategory.OTHER,
];

export interface IViewerEmailMetadata {
  originalFrom?: string | null;
  originalTo?: string | null;
  originalSubject?: string | null;
}

export interface IViewerDocument {
  id: string;
  name?: string | null;
  mimeType?: string | null;
  sequenceNumber?: number | null;
  assetUrl: string;
  previewUrl?: string | null;
  emailMetadata?: IViewerEmailMetadata | null;
}

export interface ICategorizedGroup {
  category: DocumentCategory;
  documents: IViewerDocument[];
}

export interface IViewerMetadata {
  source: string;
  senderIdentifier: string | null;
  date: string | null;
}

export function getDocumentCategory(mimeType: string | null | undefined): DocumentCategory {
  if (!mimeType) {
    return DocumentCategory.OTHER;
  }
  const lower = mimeType.toLowerCase();
  if (lower === 'application/pdf') {
    return DocumentCategory.PDF;
  }
  if (lower.startsWith('image/')) {
    return DocumentCategory.IMAGE;
  }
  if (lower === 'text/html' || lower === 'text/plain') {
    return DocumentCategory.EMAIL;
  }
  return DocumentCategory.OTHER;
}

export function isPreviewable(mimeType: string | null | undefined): boolean {
  return getDocumentCategory(mimeType) !== DocumentCategory.OTHER;
}

/**
 * Pick the best document to auto-select when the viewer opens.
 * Prefers PDFs, then images, then email — matching CATEGORY_ORDER.
 */
export function pickDefaultDocument(documents: ReadonlyArray<IViewerDocument>): IViewerDocument | undefined {
  for (const cat of CATEGORY_ORDER) {
    if (cat === DocumentCategory.OTHER) {
      continue;
    }
    const match = documents.find((d) => getDocumentCategory(d.mimeType) === cat);
    if (match) {
      return match;
    }
  }
  return undefined;
}

function sortBySequence(a: IViewerDocument, b: IViewerDocument): number {
  const aSeq = a.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
  const bSeq = b.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
  return aSeq - bSeq;
}

export function categorizeDocuments(documents: ReadonlyArray<IViewerDocument>): ICategorizedGroup[] {
  const grouped = new Map<DocumentCategory, IViewerDocument[]>();

  for (const doc of documents) {
    const category = getDocumentCategory(doc.mimeType);
    const existing = grouped.get(category) ?? [];
    existing.push(doc);
    grouped.set(category, existing);
  }

  for (const docs of grouped.values()) {
    docs.sort(sortBySequence);
  }

  return CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((cat) => ({
    category: cat,
    documents: grouped.get(cat)!,
  }));
}

export function getDocumentDisplayName(doc: IViewerDocument, indexInCategory: number): string {
  if (doc.name) {
    return doc.name;
  }

  const category = getDocumentCategory(doc.mimeType);
  const num = doc.sequenceNumber ?? indexInCategory + 1;

  switch (category) {
    case DocumentCategory.PDF:
      return `PDF - Page ${num}`;
    case DocumentCategory.IMAGE:
      return `Image ${num}`;
    case DocumentCategory.EMAIL:
      return doc.mimeType?.toLowerCase() === 'text/plain' ? `Email (text)` : `Email`;
    default:
      return `Document ${num}`;
  }
}
